import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useRouter } from 'next/router'
import { Dropdown } from '@pretamanger/component-library'
import Translation, {
  hasTranslationFor,
  translate
} from '#src/common/components/translation'
import {
  NutritionalWrapper,
  StyledTable,
  NutritionalHeading,
  HeadingContainer
} from './styles'
import { product } from '#src/common/propTypes'
import { mapLocale } from '#lib/locale'
import { nutritionalTable } from '#constants'
import { hasFoodCustomisationGroups } from '#src/products/util'
import NutritionalDisclaimer from '#src/products/product-details-page/components/nutritionals/disclaimer'

const getTranslatedHeadings = (headings, product, locale) =>
  headings.reduce(
    (acc, h) =>
      hasTranslationFor(`product.nutritionals.table.${h}`, locale)
        ? acc.concat([
            <Translation
              key={`product.nutritionals.table.${h}`}
              id={`product.nutritionals.table.${h}`}
              tokens={product}
            />
          ])
        : acc,
    []
  )

const getRows = (nutritionals, headings, headingToValueMap, locale) =>
  nutritionals.reduce(
    (acc, nutritional) =>
      acc.concat([
        headings.reduce(
          (acc, prop) =>
            hasTranslationFor(`product.nutritionals.table.${prop}`, locale)
              ? acc.concat([
                  getNutritionalValue(nutritional, headingToValueMap[prop])
                ])
              : acc,
          []
        )
      ]),
    []
  )

const getNutritionalValue = (nutritional, name) =>
  String(nutritional.find(n => n.name === name)?.value || '')

const mapSelectOptions = (options = []) =>
  options
    .filter(option => !option.isDefault)
    .map(({ id, name }) => ({
      id: id || name,
      value: id || name,
      label: name
    }))

const findSelectedOption = (option, collection) =>
  collection.find(({ id }) => id === option)

const Nutritionals = ({ product, className }) => {
  const { locale } = useRouter()
  const { customisationOptions = [] } = product
  const [currentProduct, setCurrentProduct] = useState(product)
  const customisationProducts = customisationOptions.reduce((acc, curr) => {
    acc = [...acc, ...curr.options]
    return acc
  }, [])
  const headings =
    locale === 'en-US'
      ? nutritionalTable.us.headings
      : nutritionalTable.default.headings

  const headingToValueMap =
    locale === 'en-US'
      ? nutritionalTable.us.values
      : nutritionalTable.default.values

  const productOptions = hasFoodCustomisationGroups(product)
    ? mapSelectOptions(customisationProducts)
    : []

  if (productOptions.length) {
    productOptions.unshift({
      id: product.id,
      value: product.id,
      label: product.name
    })
  }
  const selectedProductId = currentProduct.id

  const handleSelect = productId => {
    const selectedProduct = findSelectedOption(productId, customisationProducts)
    setCurrentProduct(selectedProduct || product)
  }

  return (
    product.nutritionals.length > 0 && (
      <NutritionalWrapper
        data-testid='nutritional-container'
        className={className}
      >
        <HeadingContainer>
          <NutritionalHeading level='h2' styleOverride='headingXs'>
            <Translation id='product.nutritionals.heading' />
          </NutritionalHeading>
          {!!productOptions.length && (
            <Dropdown
              id='product-nutritionals'
              name='product-nutritionals'
              current={selectedProductId}
              label={
                translate('product.nutritionals.selector.label', locale).value
              }
              hideLabel
              onSelect={handleSelect}
              options={productOptions}
            />
          )}
        </HeadingContainer>
        <NutritionalDisclaimer />
        <StyledTable
          headings={getTranslatedHeadings(
            headings,
            currentProduct,
            mapLocale(locale, true)
          )}
          rows={getRows(
            currentProduct.nutritionals,
            headings,
            headingToValueMap,
            mapLocale(locale, true)
          )}
          hasRowHeaders
        />
      </NutritionalWrapper>
    )
  )
}

Nutritionals.propTypes = {
  className: PropTypes.string,
  product
}

Nutritionals.defaultProps = {
  className: '',
  product: {
    nutritionals: [],
    customisationOptions: []
  }
}

export default Nutritionals
